import Swiper, { Navigation } from 'swiper';

const insightsStates = new WeakMap();

const getCards = (wrapperEl) => Array.from(wrapperEl.children)
  .map((slide) => slide.querySelector('.blog-card'))
  .filter(Boolean);

const createSlide = (content) => {
  const slide = document.createElement('div');

  slide.className = 'swiper-slide insights-sec__slide';
  slide.append(content);

  return slide;
};

const getSliderGap = (swiperEl) => Number.parseFloat(
  window.getComputedStyle(swiperEl).getPropertyValue('--insights-swiper-gap'),
) || 12;

const renderSlides = (state, layout) => {
  const fragment = document.createDocumentFragment();
  const isStackedLayout = layout === 'stacked';

  if (!isStackedLayout) {
    state.cards.forEach((card) => {
      fragment.append(createSlide(card));
    });

    state.wrapperEl.replaceChildren(fragment);
    return;
  }

  for (let index = 0; index < state.cards.length; index += 1) {
    const card = state.cards[index];

    if (card.classList.contains('full-card')) {
      fragment.append(createSlide(card));
      continue;
    }

    const stack = document.createElement('div');

    stack.className = 'insights-sec__slide-stack';
    stack.append(card);

    const nextCard = state.cards[index + 1];

    if (nextCard && !nextCard.classList.contains('full-card')) {
      stack.append(nextCard);
      index += 1;
    }

    fragment.append(createSlide(stack));
  }

  state.wrapperEl.replaceChildren(fragment);
};

const getActiveCardIndex = (state) => {
  const activeSlide = state.swiper?.slides?.[state.swiper.activeIndex];
  const activeCard = activeSlide?.querySelector('.blog-card');

  return Math.max(0, state.cards.indexOf(activeCard));
};

const getSlideIndexByCard = (state, cardIndex) => Array.from(state.wrapperEl.children)
  .findIndex((slide) => slide.contains(state.cards[cardIndex]));

const createSwiper = (state, isMobile) => {
  const { swiperEl, wrapper, currentCardIndex } = state;
  const nextEl = wrapper.querySelector('.swiper-button-next');
  const prevEl = wrapper.querySelector('.swiper-button-prev');

  state.swiper = new Swiper(swiperEl, {
    modules: [Navigation],
    observer: true,
    observeParents: true,
    slidesPerView: isMobile ? 1 : 3,
    slidesPerGroup: 1,
    spaceBetween: getSliderGap(swiperEl),
    speed: 500,
    threshold: 10,
    watchOverflow: true,
    navigation: nextEl && prevEl ? {
      nextEl,
      prevEl,
    } : undefined,
    breakpoints: {
      641: {
        spaceBetween: 16,
      },
      768: {
        spaceBetween: 18,
      },
      820: {
        spaceBetween: 20,
      },
      951: {
        spaceBetween: 22,
      },
      1101: {
        spaceBetween: 24,
      },
      1281: {
        spaceBetween: 27,
      },
    },
  });

  const slideIndex = getSlideIndexByCard(state, currentCardIndex);

  if (slideIndex > 0) {
    state.swiper.slideTo(slideIndex, 0);
  }
};

const rebuildInsightsSwiper = (state) => {
  const isMobile = state.mediaQuery.matches;
  const layout = isMobile || state.cards.length <= 3 ? 'single' : 'stacked';

  if (state.swiper && !state.swiper.destroyed && state.layout === layout && state.isMobile === isMobile) {
    state.swiper.update();
    return;
  }

  state.currentCardIndex = getActiveCardIndex(state);
  state.swiper?.destroy(true, true);
  state.swiper = null;
  state.layout = layout;
  state.isMobile = isMobile;

  renderSlides(state, layout);
  createSwiper(state, isMobile);
};

const initInsightsSwiper = (scope = document) => {
  const wrappers = scope.querySelectorAll('.insights-swiper-wr');

  wrappers.forEach((wrapper) => {
    if (insightsStates.has(wrapper)) {
      rebuildInsightsSwiper(insightsStates.get(wrapper));
      return;
    }

    const swiperEl = wrapper.querySelector('.swiper');
    const wrapperEl = swiperEl?.querySelector('.swiper-wrapper');

    if (!swiperEl || !wrapperEl) {
      return;
    }

    const cards = getCards(wrapperEl);

    if (!cards.length) {
      return;
    }

    const state = {
      wrapper,
      swiperEl,
      wrapperEl,
      cards,
      swiper: null,
      layout: null,
      isMobile: null,
      currentCardIndex: 0,
      mediaQuery: window.matchMedia('(max-width: 640px)'),
    };

    state.mediaQuery.addEventListener('change', () => rebuildInsightsSwiper(state));
    insightsStates.set(wrapper, state);
    rebuildInsightsSwiper(state);
  });
};

initInsightsSwiper();
window.initInsightsSwiper = initInsightsSwiper;
